import axios from "axios";
import { Franchise, FranchiseStatus } from "../types/Franchise";
import { Listen360Location } from "../types/Listen360Location";
import { Listen360User } from "../types/Listen360User";
import { LocationMapping } from "../types/LocationMapping";
import { Listen360ApiUser } from "../types/Listen360ApiUser";
import { SyncJob } from "../types/SyncJob";

// API

const api = axios.create({
    baseURL: '/api/v0/',
    withCredentials: true,
});

export function jobberAccountName(state: string|null) {
    return api.get("jobber/account_name", {
        headers: {
            'X-INTEGRATION-STATE': state
        }
    });
}

export function jobberAuthorize(code: string|null, state: string|null) {
    return api.post("jobber/authorize", {}, {
        headers: {
            'X-JOBBER-CODE': code,
            'X-INTEGRATION-STATE': state,
        }
    });
}

// ADMIN

export const ADMIN_SESSION_TOKEN_KEY = "admin_session_token";

const admin = axios.create({
    baseURL: '/api',
    withCredentials: true,
});

admin.interceptors.request.use((config) => {
    const token = sessionStorage.getItem(ADMIN_SESSION_TOKEN_KEY);
    if (token) {
        config.headers["API_SESSIONS_TOKEN"] = token;
    }
    return config;
});

admin.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401 || error.response?.status === 403) {
            sessionStorage.removeItem(ADMIN_SESSION_TOKEN_KEY);
            if (window.location.pathname !== "/login") {
                window.location.href = "/login";
            }
        }
        return Promise.reject(error);
    }
);

export function sendAdminLoginEmail(adminEmail: string) {
    return admin.post("/auth/login", { email: adminEmail });
}

export function verifyLoginToken(token: string) {
    return admin.post("/auth/verify", { token });
}

export async function getConnectedFranchises(): Promise<Array<Franchise>> {
    const response = await admin.get("/franchise/list");
    return response.data;
}

export function updateFranchiseStatus(franchiseId: string, currentStatus: FranchiseStatus, newStatus: FranchiseStatus) {
    const action = newStatus === FranchiseStatus.Active
        ? (currentStatus === FranchiseStatus.Pending ? "approve" : "activate")
        : "deactivate";
    return admin.post(`/franchise/${franchiseId}/${action}`);
}

export function disconnectFranchise(franchiseId: string) {
    return admin.post(`/franchise/${franchiseId}/disconnect`);
}

export async function getListen360Users(): Promise<Listen360User[]> {
    const response = await admin.get("/user/list");
    return response.data;
}

export async function getListen360Locations(): Promise<Listen360Location[]> {
    const response = await admin.get("/location/list");
    return response.data;
}

export function attachLocationToUser(userId: string, locationId: string) {
    return admin.post(`/user/${userId}/location/${locationId}/attach`);
}

export function detachLocationFromUser(userId: string, locationId: string) {
    return admin.post(`/user/${userId}/location/${locationId}/detach`);
}

export async function createLocationMapping(franchiseId: string, locationId: string): Promise<string> {
    const response = await admin.post("/integration/location_mapping/create", {
        franchise_id: franchiseId,
        location_id: locationId,
    });
    return response.data.mapping_id;
}

export function deleteLocationMapping(mappingId: string) {
    return admin.post(`/integration/location_mappings/${mappingId}/delete`);
}

export async function getLocationMapping(): Promise<LocationMapping> {
    const response = await admin.get("/integration/location_mapping");
    return response.data;
}

export async function getListen360ApiUsers(): Promise<Listen360ApiUser[]> {
    const response = await admin.get("/user/list");
    return response.data.map((u: any) => ({ id: u.id, name: u.name, token_preview: u.token_preview }));
}

export async function addListen360ApiUser(name: string, token: string): Promise<Listen360ApiUser> {
    const response = await admin.post("/user/create", { name, api_token: token });
    return { id: response.data.id, name: response.data.name, token_preview: response.data.token_preview };
}

export async function updateListen360ApiUserToken(userId: string, token: string): Promise<void> {
    await admin.patch(`/user/${userId}`, { token });
}

export function syncJobs(startDate: Date, endDate: Date) {
    // clamp the date range to make it fully inclusive
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    return admin.post("/integration/sync_jobs", {
        start_date: start.toISOString(),
        end_date: end.toISOString(),
    });
}

export async function getSyncHistory(): Promise<SyncJob[]> {
    const response = await admin.get("/integration/sync_history");
    return response.data;
}

export default api;
